import { cached, tracked } from "@glimmer/tracking";
import { action } from "@ember/object";
import Service, { service } from "@ember/service";
import { ajax } from "discourse/lib/ajax";
import { withoutPrefix } from "discourse/lib/get-url";
import selection from "../lib/community-navigation-selection";

const EXCLUDED_PATHS = [
  "/admin",
  "/login",
  "/signup",
  "/wizard",
  "/embed",
  "/session",
];

export default class CommunityNavigationService extends Service {
  @service communityGameOrder;
  @service siteSettings;

  @tracked tree;
  @tracked active = false;
  @tracked primaryKey = "public";
  @tracked secondaryKey = "public";
  @tracked showSecondary = true;
  @tracked activeItemKey;
  @tracked mobileOpen = false;

  loadingPromise;
  mobileTrigger;

  @action
  openMobile() {
    this.mobileTrigger = document.activeElement;
    this.mobileOpen = true;
    document.body.classList.add("has-community-navigation-drawer");
    window.requestAnimationFrame(() => {
      document.querySelector(".community-navigation-drawer")?.focus();
    });
  }

  @action
  closeMobile({ restoreFocus = true } = {}) {
    if (!this.mobileOpen) {
      return;
    }

    this.mobileOpen = false;
    document.body.classList.remove("has-community-navigation-drawer");
    const trigger = this.mobileTrigger;
    this.mobileTrigger = null;
    if (restoreFocus) {
      window.requestAnimationFrame(() => trigger?.focus());
    }
  }

  load() {
    this.loadingPromise ||= Promise.all([
      ajax("/community-navigation.json"),
      this.siteSettings.discourse_mod_market_enabled
        ? ajax("/mod-market-api/v1/games.json").catch(() => ({ games: [] }))
        : Promise.resolve({ games: [] }),
    ]).then(([tree, gameResponse]) => {
      const games = gameResponse.games || [];
      const gamesByKey = new Map(games.map((game) => [game.key, game]));
      const primary = tree.primary.map((item) => {
        if (item.key !== "games") {
          return item;
        }

        return {
          ...item,
          children: item.children.map((child) => ({
            ...child,
            ...gamesByKey.get(child.game_key),
          })),
        };
      });
      const gameGroup = primary.find((item) => item.key === "games");
      this.communityGameOrder.setGames(gameGroup?.children || []);
      this.tree = { ...tree, primary, games };
      return this.tree;
    });
    return this.loadingPromise;
  }

  get games() {
    return this.tree?.games || [];
  }

  get activeGame() {
    const prefix = "game.";
    if (!this.primaryKey?.startsWith(prefix)) {
      return;
    }

    const gameKey = this.primaryKey.slice(prefix.length);
    return this.games.find((game) => game.key === gameKey);
  }

  @cached
  get primaryItemsByKey() {
    const items = (this.tree?.primary || []).flatMap((item) =>
      item.kind === "group" ? item.children || [] : [item]
    );

    return new Map(
      items.map((item) => {
        const primaryKey = item.game_key
          ? `game.${item.game_key}`
          : item.key;

        return [
          primaryKey,
          {
            key: primaryKey,
            label: item.name || item.label,
            href: item.href,
          },
        ];
      })
    );
  }

  virtualParentForCategory(category) {
    if (!this.tree) {
      return;
    }

    const mappings = this.tree.mappings?.categories || {};
    let mappingKey = category?.custom_fields?.community_navigation_key;
    let mapping = mappings[mappingKey];

    if (!mapping && category?.id !== undefined) {
      const entry = Object.entries(mappings).find(
        ([, value]) => Number(value.id) === Number(category.id)
      );
      mappingKey = entry?.[0];
      mapping = entry?.[1];
    }

    const context = mapping
      ? this.#contextForMapping(mappingKey, mapping)
      : { primaryKey: "public" };

    return this.primaryItemsByKey.get(context.primaryKey);
  }

  async update(path, container) {
    const url = new URL(path, window.location.origin);
    const pathname = withoutPrefix(url.pathname);
    const usesPrivateMessageLayout = this.#usesPrivateMessageLayout(
      pathname,
      container
    );
    const usesCustomLayout = !this.#isExcludedPath(pathname);

    this.active = usesCustomLayout;
    this.showSecondary =
      usesCustomLayout && !this.#isModMarketPath(pathname) && !usesPrivateMessageLayout;
    document.body.classList.toggle(
      "community-navigation-layout",
      usesCustomLayout
    );
    document.body.classList.toggle(
      "has-community-secondary-navigation",
      this.showSecondary
    );
    document.body.classList.toggle(
      "community-system-message-layout",
      usesCustomLayout && usesPrivateMessageLayout
    );
    if (!usesCustomLayout) {
      document.body.classList.remove(
        "has-community-game-context",
        "has-community-game-background"
      );
    }

    const context = await this.#contextFor(pathname, url.searchParams, container);

    this.active = Boolean(context);

    if (!context) {
      this.closeMobile({ restoreFocus: false });
      this.showSecondary = false;
      document.body.classList.remove(
        "community-navigation-layout",
        "has-community-secondary-navigation",
        "has-community-game-context",
        "has-community-game-background",
        "community-system-message-layout"
      );
      return;
    }

    selection.primaryKey = context.primaryKey;
    this.primaryKey = context.primaryKey;
    this.secondaryKey = context.secondaryKey;
    this.showSecondary = context.showSecondary !== false;
    this.activeItemKey = context.activeItemKey;
    document.body.classList.add("community-navigation-layout");
    document.body.classList.toggle(
      "community-system-message-layout",
      this.#usesPrivateMessageLayout(pathname, container)
    );
    document.body.classList.toggle(
      "has-community-game-context",
      Boolean(this.activeGame)
    );
    document.body.classList.toggle(
      "has-community-game-background",
      Boolean(this.activeGame?.cover_url)
    );
    document.body.classList.toggle(
      "has-community-secondary-navigation",
      this.showSecondary && Boolean(this.secondary)
    );
  }

  get secondary() {
    if (this.showSecondary) {
      return this.tree?.secondary?.[this.secondaryKey];
    }
  }

  async #contextFor(pathname, searchParams, container) {
    if (this.#isExcludedPath(pathname)) {
      return null;
    }

    if (this.#usesPrivateMessageLayout(pathname, container)) {
      return {
        primaryKey: "public",
        secondaryKey: "public",
        showSecondary: false,
      };
    }

    const tree = await this.load();

    if (pathname === "/mods" || pathname.startsWith("/mods/")) {
      return {
        primaryKey: "mods",
        secondaryKey: "mods",
        showSecondary: false,
      };
    }
    if (pathname === "/mod-community") {
      return { primaryKey: "mod", secondaryKey: "mod" };
    }
    if (pathname === "/creator-community") {
      return tree.secondary.creator
        ? { primaryKey: "creator", secondaryKey: "creator" }
        : null;
    }
    if (pathname === "/creator/resources") {
      return tree.secondary.creator
        ? {
            primaryKey: "creator",
            secondaryKey: "creator",
            activeItemKey: "creator.resources",
          }
        : null;
    }
    if (pathname === "/media/feedback") {
      return {
        primaryKey: "mod",
        secondaryKey: "mod",
        activeItemKey: "media.feedback",
      };
    }

    const gameMatch = pathname.match(/^\/games\/([^/]+)$/);
    if (gameMatch) {
      const key = `game.${gameMatch[1]}`;
      return { primaryKey: key, secondaryKey: key };
    }

    if (pathname === "/media") {
      const gameKey = searchParams.get("game");
      if (!gameKey) {
        return { primaryKey: "public", secondaryKey: "public" };
      }

      const key = `game.${gameKey}`;
      return {
        primaryKey: key,
        secondaryKey: key,
        activeItemKey: `media.${gameKey}`,
      };
    }

    const categoryContext = this.#categoryContext(pathname, tree);
    if (categoryContext) {
      return categoryContext;
    }

    const channelContext = this.#channelContext(pathname, tree);
    if (pathname.startsWith("/chat/")) {
      return (
        channelContext || {
          primaryKey: "public",
          secondaryKey: "public",
        }
      );
    }

    const topic = this.#topicFor(pathname, container);

    const topicContext = topic?.category_id
      ? this.#categoryContextById(topic.category_id, tree)
      : null;
    return (
      topicContext || {
        primaryKey: "public",
        secondaryKey: "public",
        activeItemKey: pathname === "/latest" ? "all_topics" : undefined,
      }
    );
  }

  #isExcludedPath(pathname) {
    return (
      EXCLUDED_PATHS.some(
        (prefix) => pathname === prefix || pathname.startsWith(`${prefix}/`)
      ) ||
      pathname.includes("/messages") ||
      pathname.startsWith("/chat/dm") ||
      pathname.startsWith("/chat/new-message")
    );
  }

  #isPrivateMessage(pathname, container) {
    const isTopicPath =
      pathname.startsWith("/t/") || pathname.startsWith("/n/");

    return (
      isTopicPath &&
      (document.body.classList.contains("archetype-private_message") ||
        this.#topicFor(pathname, container)?.isPrivateMessage)
    );
  }

  #usesPrivateMessageLayout(pathname, container) {
    return this.#isPrivateMessage(pathname, container);
  }

  #isModMarketPath(pathname) {
    return pathname === "/mods" || pathname.startsWith("/mods/");
  }

  #topicFor(pathname, container) {
    if (pathname.startsWith("/t/") || pathname.startsWith("/n/")) {
      return container.lookup("controller:topic")?.model;
    }
  }

  #categoryContext(pathname, tree) {
    const match = Object.entries(tree.mappings.categories).find(
      ([, mapping]) => {
        const hrefs = [mapping.href, mapping.context?.alternate_href].filter(
          Boolean
        );
        return hrefs.some(
          (href) => {
            const mappedPath = withoutPrefix(
              new URL(href, window.location.origin).pathname
            );
            return (
              pathname === mappedPath || pathname.startsWith(`${mappedPath}/`)
            );
          }
        );
      }
    );
    return match && this.#contextForMapping(match[0], match[1]);
  }

  #categoryContextById(categoryId, tree) {
    const match = Object.entries(tree.mappings.categories).find(
      ([, mapping]) => mapping.id === categoryId
    );
    return match && this.#contextForMapping(match[0], match[1]);
  }

  #channelContext(pathname, tree) {
    const match = Object.entries(tree.mappings.channels).find(([, mapping]) =>
      pathname.startsWith(
        withoutPrefix(new URL(mapping.href, window.location.origin).pathname)
      )
    );
    return match && this.#contextForMapping(match[0], match[1]);
  }

  #contextForMapping(mappingKey, mapping) {
    if (mapping.context) {
      return {
        primaryKey: mapping.context.primary_key,
        secondaryKey: mapping.context.secondary_key,
        activeItemKey: mapping.context.active_item_key,
      };
    }

    return this.#contextForMappingKey(mappingKey, mappingKey);
  }

  #contextForMappingKey(mappingKey, activeItemKey) {
    if (
      mappingKey.startsWith("category.public.") ||
      mappingKey.startsWith("channel.public.")
    ) {
      return { primaryKey: "public", secondaryKey: "public", activeItemKey };
    }
    if (
      mappingKey.startsWith("category.mod.") ||
      mappingKey.startsWith("channel.mod.")
    ) {
      return { primaryKey: "mod", secondaryKey: "mod", activeItemKey };
    }
    if (
      mappingKey.startsWith("category.creator.") ||
      mappingKey.startsWith("channel.creator.")
    ) {
      return { primaryKey: "creator", secondaryKey: "creator", activeItemKey };
    }

    const gameKey = mappingKey.split(".")[2];
    const key = `game.${gameKey}`;
    return { primaryKey: key, secondaryKey: key, activeItemKey };
  }
}
